// Capturing a signature: type a name (drawn in a handwriting face) or draw one,
// tick the consent wording, press "I Agree and Sign".
//
// Used by Shelter Stone's signer, by personnel under My events, and on the public
// page an event organiser reaches by link — so it depends on nothing but React and
// MUI. Either way the result is a PNG (as a data URL) plus the printed name: the
// image goes into the document's signature block, the name beside it, and the
// server records which method was used.
import { useEffect, useRef, useState } from "react";
import {
  Box, Button, Checkbox, CircularProgress, FormControlLabel, Stack, TextField,
  ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import DrawIcon from "@mui/icons-material/Draw";
import KeyboardIcon from "@mui/icons-material/Keyboard";

const WIDTH = 600;
const HEIGHT = 170;
const SCRIPT_FONT = "'Segoe Script', 'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive";

// The typed name drawn in a handwriting face, shrunk to fit.
function typedSignature(name) {
  const canvas = document.createElement("canvas");
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext("2d");
  let size = 64;
  ctx.font = `${size}px ${SCRIPT_FONT}`;
  while (size > 20 && ctx.measureText(name).width > WIDTH - 40) {
    size -= 4;
    ctx.font = `${size}px ${SCRIPT_FONT}`;
  }
  ctx.fillStyle = "#10223f";
  ctx.textBaseline = "middle";
  ctx.fillText(name, 20, HEIGHT / 2);
  return canvas.toDataURL("image/png");
}

/**
 * Props:
 *  - defaultName:  pre-fills the printed name
 *  - consentText:  the wording the signer agrees to (from the server)
 *  - submitLabel:  defaults to "I Agree and Sign"
 *  - busy:         a signature is being submitted
 *  - onSign({ signer_name, method, signature_image, agree })
 */
export default function SignaturePad({ defaultName = "", consentText, submitLabel, busy, onSign }) {
  const [name, setName] = useState(defaultName);
  const [method, setMethod] = useState("typed");
  const [agree, setAgree] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => { setName(defaultName); }, [defaultName]);

  const ctx = () => {
    const c = canvasRef.current;
    if (!c) return null;
    const g = c.getContext("2d");
    g.lineWidth = 3;
    g.lineCap = "round";
    g.lineJoin = "round";
    g.strokeStyle = "#10223f";
    return g;
  };

  // Pointer position in canvas pixels — the canvas is drawn at a fixed size and
  // scaled by CSS to the width available.
  const point = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };

  const start = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    const g = ctx(); const p = point(e);
    g.beginPath(); g.moveTo(p.x, p.y);
    g.lineTo(p.x + 0.1, p.y + 0.1); g.stroke();
    setHasInk(true);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const g = ctx(); const p = point(e);
    g.lineTo(p.x, p.y); g.stroke();
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    c?.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  const trimmed = name.trim();
  const ready = trimmed && agree && (method === "typed" || hasInk) && !busy;

  const submit = () => {
    if (!ready) return;
    const image = method === "typed" ? typedSignature(trimmed) : canvasRef.current.toDataURL("image/png");
    onSign({ signer_name: trimmed, method, signature_image: image, agree });
  };

  return (
    <Stack spacing={2}>
      <TextField label="Your full name" value={name} onChange={e => setName(e.target.value)}
                 required fullWidth autoComplete="name"
                 helperText="Printed beside your signature." />
      {/* Switching remounts the canvas empty, so any earlier drawing is gone. */}
      <ToggleButtonGroup exclusive size="small" value={method}
                         onChange={(_, v) => { if (v) { setMethod(v); setHasInk(false); } }}>
        <ToggleButton value="typed"><KeyboardIcon fontSize="small" sx={{ mr: 1 }} />Type</ToggleButton>
        <ToggleButton value="drawn"><DrawIcon fontSize="small" sx={{ mr: 1 }} />Draw</ToggleButton>
      </ToggleButtonGroup>

      {method === "typed" ? (
        <Box sx={{ border: "1px dashed", borderColor: "divider", borderRadius: 1, px: 2, py: 1,
                   minHeight: 80, display: "flex", alignItems: "center" }}>
          <Typography sx={{ fontFamily: SCRIPT_FONT, fontSize: { xs: 30, sm: 40 }, color: "#10223f",
                            overflow: "hidden", whiteSpace: "nowrap" }}>
            {trimmed || <Box component="span" sx={{ color: "text.disabled", fontSize: 18,
                                                    fontFamily: "inherit" }}>Your signature</Box>}
          </Typography>
        </Box>
      ) : (
        <Box>
          <Box component="canvas" ref={canvasRef} width={WIDTH} height={HEIGHT}
               onPointerDown={start} onPointerMove={move} onPointerUp={end}
               onPointerCancel={end} onPointerLeave={end}
               aria-label="Signature drawing area"
               sx={{ width: "100%", maxWidth: WIDTH, aspectRatio: `${WIDTH} / ${HEIGHT}`,
                     border: "1px dashed", borderColor: "divider", borderRadius: 1,
                     touchAction: "none", cursor: "crosshair", display: "block",
                     bgcolor: "background.paper" }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Sign with your finger, a stylus or the mouse.
            </Typography>
            <Button size="small" onClick={clear} disabled={!hasInk}>Clear</Button>
          </Box>
        </Box>
      )}

      <FormControlLabel
        control={<Checkbox checked={agree} onChange={e => setAgree(e.target.checked)} />}
        label={<Typography variant="body2">{consentText}</Typography>}
        sx={{ alignItems: "flex-start", "& .MuiCheckbox-root": { pt: 0.25 } }} />

      <Box>
        <Button variant="contained" size="large" disabled={!ready} onClick={submit}
                startIcon={busy ? <CircularProgress size={18} color="inherit" /> : undefined}>
          {busy ? "Signing…" : (submitLabel || "I Agree and Sign")}
        </Button>
      </Box>
    </Stack>
  );
}
